import { useState } from 'react';
import { TeamState } from '@/types';
import { TeamLogo } from '@/components/TeamLogo';

interface FirstServingTeamSelectionProps {
  teamA: TeamState;
  teamB: TeamState;
  setNumber: number;
  onSelect: (teamId: string) => void;
  onCancel?: () => void;
}

export default function FirstServingTeamSelection({
  teamA,
  teamB,
  setNumber,
  onSelect,
  onCancel,
}: FirstServingTeamSelectionProps) {
  const [selectingId, setSelectingId] = useState<string | null>(null);

  const handleSelect = (teamId: string) => {
    setSelectingId(teamId);
    onSelect(teamId);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-2xl p-6 max-w-md w-full animate-fade-in">
        <h2 className="text-xl font-bold text-text-primary text-center mb-2">
          START SET {setNumber}
        </h2>
        <p className="text-text-secondary text-center mb-6">
          Select which team will serve first
        </p>

        <div className="space-y-3">
          {/* Team A Button */}
          <button
            onClick={() => handleSelect(teamA.id)}
            disabled={selectingId !== null}
            className="w-full py-5 px-4 rounded-lg font-bold text-lg transition-all active:scale-[0.98] flex items-center justify-center gap-3"
            style={{
              backgroundColor: selectingId === teamA.id ? teamA.color : `${teamA.color}20`,
              color: selectingId === teamA.id ? '#ffffff' : teamA.color,
              border: `3px solid ${teamA.color}`,
              boxShadow: selectingId === teamA.id ? `0 4px 12px ${teamA.color}60` : undefined,
            }}
          >
            <TeamLogo team={teamA} size="sm" />
            <span>{teamA.name.toUpperCase()}</span>
            {selectingId === teamA.id && (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin ml-2" />
            )}
          </button>

          {/* VS Divider */}
          <div className="flex items-center justify-center">
            <div className="h-px bg-border flex-1" />
            <span className="text-text-muted px-4 text-sm font-semibold">VS</span>
            <div className="h-px bg-border flex-1" />
          </div>

          {/* Team B Button */}
          <button
            onClick={() => handleSelect(teamB.id)}
            disabled={selectingId !== null}
            className="w-full py-5 px-4 rounded-lg font-bold text-lg transition-all active:scale-[0.98] flex items-center justify-center gap-3"
            style={{
              backgroundColor: selectingId === teamB.id ? teamB.color : `${teamB.color}20`,
              color: selectingId === teamB.id ? '#ffffff' : teamB.color,
              border: `3px solid ${teamB.color}`,
              boxShadow: selectingId === teamB.id ? `0 4px 12px ${teamB.color}60` : undefined,
            }}
          >
            <TeamLogo team={teamB} size="sm" />
            <span>{teamB.name.toUpperCase()}</span>
            {selectingId === teamB.id && (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin ml-2" />
            )}
          </button>
        </div>

        {onCancel && (
          <button
            onClick={onCancel}
            disabled={selectingId !== null}
            className="w-full mt-4 py-3 px-4 rounded-lg text-sm font-semibold text-text-secondary bg-surface-raised border border-border hover:bg-surface transition-colors disabled:opacity-50"
          >
            CANCEL
          </button>
        )}
      </div>
    </div>
  );
}
